import { client } from '@api/rest/client';
import type {
  DataSourceDataDTO,
  DataSourceDetailsDTO,
  DataSourceFileContentDTO,
  LLMBlockSelectionDTO,
  MoveDataSourceDTO,
  UpdateDataSourceDTO,
} from '@conversed/rest/src/contracts/data-sources/data-sources.dto';
import {
  DocumentType,
  ThirdPartySource,
} from '@conversed/rest/src/contracts/data-sources/data-sources.enums';
import { LLMUsageCheckResultStatus } from '@conversed/rest/src/contracts/llm-usages/check-results';
import type { TConversationDetailsSchema } from '@conversed/rest/src/contracts/llm-usages/llm-usages.contract';
import ErrorCodes from '@conversed/shared/src/errors/error-codes';
import { htmlToExcelText } from '@conversed/shared/src/helpers/utils';
import { useModal } from '@hooks/modal/useModal';
import { useMainSelector } from '@hooks/useMainSelector';
import { mainActions } from '@store/reducers/mainHandler';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import XLSX from 'xlsx-js-style';
import {
  toastErrorTranslated,
  toastSuccessTranslated,
} from '../../helpers/toast';
import { useFileDownload } from '../useFileDownload';
import { useDataSourceFolderActions } from './useDataSourceFolderActions';
import { useDataSources } from './useDataSources';

type TTrainDataSourceBody = {
  key: string;
  documentType: DocumentType;
  lastSuccessfulTrainingStartedAt: string;
  automaticallyIncludeInLLM: boolean;
  fileType: string;
  thirdPartySource?: ThirdPartySource;
  label?: string;
  name?: string;
  selectedLLMBlocks?: LLMBlockSelectionDTO[];
};

export const useDataSourceActions = () => {
  const dispatch = useDispatch();
  const { downloadFileByKey, downloadFileFromBase64 } = useFileDownload();
  const { fetchDataSourceFolders } = useDataSourceFolderActions();
  const { showModal } = useModal();
  const { dataSources } = useMainSelector();
  const { trainFolderId } = useDataSources();

  const trainDataSource = useCallback(
    async (options: TTrainDataSourceBody) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));
        const { data: files } = await client.dataSource.vectorTrainSource({
          body: {
            ...options,
            thirdPartySource: options.thirdPartySource || null,
          },
        });
        toastSuccessTranslated('data-sources.started-training.success');

        dispatch(mainActions.setDataSources(files));
        dispatch(mainActions.setLastTrainingActionAt(new Date().toISOString()));
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const downloadDataSource = useCallback(
    async (
      _id: string,
      fileKey: string,
      thirdPartySource?: ThirdPartySource,
    ) => {
      try {
        switch (thirdPartySource) {
          case ThirdPartySource.Sharepoint:
          case ThirdPartySource.SharepointPages: {
            const { data } = await client.sharepoint.downloadFile({
              params: { id: _id },
            });

            if (!data?.fileBuffer || !data?.fileName || !data?.fileType) {
              throw new Error('Invalid file data received from SharePoint');
            }

            downloadFileFromBase64(
              data.fileBuffer,
              data.fileName,
              data.fileType,
            );
            break;
          }
          default: {
            const { data } = await client.dataSource.getDataSource({
              params: { id: _id },
            });
            downloadFileByKey(fileKey, data);
            break;
          }
        }
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      }
    },
    [downloadFileByKey, downloadFileFromBase64],
  );

  const uploadDataSource = useCallback(
    async (body: DataSourceDataDTO) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));
        const { data: files } = await client.dataSource.uploadDataSource({
          body,
        });
        toastSuccessTranslated('data-sources.upload.success');

        dispatch(mainActions.setDataSources(files));
        return files;
      } catch (error: any) {
        console.error(error);

        if (
          error.response?.data?.message === ErrorCodes.INVALID_FILE_CONTENT &&
          error.response?.data?.data
        ) {
          showModal(
            'data-source-invalid-file-content',
            '',
            error.response?.data?.data,
          );
        } else if (
          error?.response?.data?.message === ErrorCodes.ALREADY_EXISTS
        ) {
          toastErrorTranslated(
            'data-sources.upload.duplicate-file-toast-message',
            {
              fileName: body?.data?.name,
            },
          );
        } else {
          toastErrorTranslated(error);
        }

        throw error;
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch, showModal],
  );

  const createDataSourceFile = useCallback(
    async (
      name: string,
      data: TConversationDetailsSchema[],
      automaticallyIncludeInLLM: boolean,
      selectedLLMBlocks?: LLMBlockSelectionDTO[],
      // Target folder; defaults to the Train folder. Pass the Priority folder id to
      // place a priority-knowledge file.
      folderId?: string,
    ) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        const worksheetData = data.map((item) => ({
          Question: item.translation?.question ?? item.question,
          Answer: htmlToExcelText(
            item.checkResult?.status ===
              LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT
              ? item.answer
              : (item.translation?.answer ?? item.answer),
          ),
          URL: item.url,
          'Category (optional)': item?.categories || '',
          'Keywords (optional)': item?.keywords || '',
        }));

        // Convert data to XLSX
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        const xlsxData = XLSX.write(workbook, {
          bookType: 'xlsx',
          type: 'base64',
        });
        const fileType =
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const base64Prefix = `data:${fileType};base64,`;

        const files = await uploadDataSource({
          documentType: DocumentType.FAQ,
          data: {
            name: `${name}.xlsx`,
            type: fileType,
            size: xlsxData.length,
            base64: `${base64Prefix}${xlsxData}`,
          },
          name,
          canBeDownloaded: false,
          folderId: folderId ?? trainFolderId,
        });

        // If the upload is successful and the new knowledge source should be automatically added to the LLM responses, train the data source.
        if (automaticallyIncludeInLLM) {
          const uploadedFile = files?.find((file) => file.name === name);
          if (uploadedFile?.key) {
            await trainDataSource({
              key: uploadedFile.key,
              documentType: DocumentType.FAQ,
              lastSuccessfulTrainingStartedAt: '',
              automaticallyIncludeInLLM,
              fileType,
              name,
              selectedLLMBlocks,
            });
          } else {
            console.error(
              `[createDataSourceFile] Could not find uploaded file "${name}" to trigger training.`,
            );
          }
        }
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
        throw error;
      }
    },
    [dispatch, uploadDataSource, trainDataSource, trainFolderId],
  );

  const updateDataSourceDetails = useCallback(
    async (body: UpdateDataSourceDTO) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));
        const { data: files } = await client.dataSource.updateDataSourceDetails(
          {
            body,
          },
        );
        toastSuccessTranslated('data-sources.update.success');

        dispatch(mainActions.setDataSources(files));
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const moveDataSource = useCallback(
    async (_id: string, body: MoveDataSourceDTO) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));
        const { data: files } = await client.dataSource.moveDataSource({
          params: { id: _id },
          body,
        });
        toastSuccessTranslated('data-sources.move.success');
        dispatch(mainActions.setDataSources(files));
      } catch (error: any) {
        console.error(error);
        if (error?.response?.data?.message === ErrorCodes.ALREADY_EXISTS) {
          const movingFile = dataSources.files.find(
            (f) => String(f._id) === _id,
          );
          toastErrorTranslated(
            'data-sources.move.duplicate-file-toast-message',
            {
              fileName: movingFile?.name || movingFile?.key.split('/').pop(),
            },
          );
        } else {
          toastErrorTranslated(error);
        }
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch, dataSources.files],
  );

  const deleteDataSource = useCallback(
    async (_id: string) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        await client.dataSource.deleteDataSource({
          params: { id: _id },
        });

        dispatch(mainActions.deleteDataSource(_id));
        fetchDataSourceFolders();

        toastSuccessTranslated('data-sources.delete.success');
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch, fetchDataSourceFolders],
  );

  const updateExistingDataSourceFile = useCallback(
    async (
      existingFile: DataSourceDetailsDTO,
      data: DataSourceFileContentDTO[],
      automaticallyIncludeInLLM: boolean,
      selectedLLMBlocks?: LLMBlockSelectionDTO[],
    ) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        await client.dataSource.updateExistingDataSourceFile({
          body: {
            existingFile,
            data,
            automaticallyIncludeInLLM,
            selectedLLMBlocks,
          },
        });

        dispatch(mainActions.setLastTrainingActionAt(new Date().toISOString()));

        if (automaticallyIncludeInLLM) {
          toastSuccessTranslated('data-sources.started-training.success');
        }
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
        throw error;
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const getDataSources = useCallback(
    async (withoutLoadingAnimation = false) => {
      try {
        if (!withoutLoadingAnimation) {
          dispatch(mainActions.setUIState({ isLoading: true }));
        }
        const { data: files } = await client.dataSource.getAllDataSources();
        return files;
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
        return [];
      } finally {
        if (!withoutLoadingAnimation) {
          dispatch(mainActions.setUIState({ isLoading: false }));
        }
      }
    },
    [dispatch],
  );

  const fetchDataSources = useCallback(
    async (withoutLoadingAnimation = false) => {
      fetchDataSourceFolders();
      const files = await getDataSources(withoutLoadingAnimation);
      dispatch(mainActions.setDataSources(files));
    },
    [getDataSources, dispatch, fetchDataSourceFolders],
  );

  return {
    downloadDataSource,
    fetchDataSources,
    deleteDataSource,
    uploadDataSource,
    getDataSources,
    updateDataSourceDetails,
    moveDataSource,
    trainDataSource,
    createDataSourceFile,
    updateExistingDataSourceFile,
    dataSourcesState: dataSources,
  };
};
